import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import useSession from '@/hooks/useSession';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Thread,
  ThreadID,
  ThreadItem,
  ThreadStatus,
  ThreadFilters,
  DEFAULT_FILTERS,
} from '@/models/thread';

import { getToday, parseFullMessage, truncateText } from '@/utils/functions';
import { LLMService } from '@/services/llm.service';
import { ThreadContext } from './ThreadContext';
import { getWorkspaceFileURL } from '@/utils/files';

export const ThreadProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { id: routeParamID } = useParams();
  const [threadID, setThreadID] = useState<ThreadID>(routeParamID);
  const { session, refreshThreads, pushUserEvent, refreshSession } =
    useSession();
  const { threads } = session;
  const [title, setTitle] = useState<string>('');
  const [items, setItems] = useState<ThreadItem[]>([]);
  const [filters, setFilters] = useState<ThreadFilters>(DEFAULT_FILTERS);
  const [status, setStatus] = useState<ThreadStatus>('READY');
  const navigate = useNavigate();

  const resetThread = () => {
    setThreadID(undefined);
    setTitle('');
    setItems([]);
    setStatus('READY');
  };

  const handleTitleUpdate = (newTitle: string): void => {
    if (!threadID) return;

    setTitle(newTitle);
    pushUserEvent('update_thread_title');
    // Update title
  };

  const handlePromptSubmit = (
    prompt: string,
    filters?: ThreadFilters
  ): void => {
    const newThreadBlock: ThreadItem = {
      id: 'new',
      disliked: false,
      prompt,
      response: [],
      enabledCitation: true,
      queryDate: getToday(),
    };
    setItems([...items, newThreadBlock]);

    if (!threadID) {
      setFilters(filters ?? DEFAULT_FILTERS);
      pushUserEvent('submit_new_thread');
      navigate('/thread/new');
    } else {
      pushUserEvent('add_follow_up');
      addResponse(newThreadBlock);
    }
  };

  const handleCitationRequest = (block: ThreadItem): void => {
    if (block.id === 'new' || !block.id || !session.email) return;
    setStatus('CITING');
    pushUserEvent('add_citation');
    refreshSession().then(() => {
      //TODO Generate citation
    });
  };

  const addResponse = useCallback(
    (block: ThreadItem): void => {
      setStatus('FETCHING');

      LLMService.query(block.prompt, threadID)
        .then((data) => {
          const { response, sources } = data;
          setItems((current: ThreadItem[]) => {
            const item = current[current.length - 1];
            item.response = parseFullMessage(response, sources);

            item.sources = sources.map((source) => ({
              source_id: source.source_id,
              file_name: source.file_name,
              title: source.file_name,
              url: getWorkspaceFileURL(source.file_id),
              logo: '', // Placeholder for logo
            }));

            return [...current.slice(0, -1), item];
          });

          if (typeof threadID === 'undefined') {
            setThreadID(data.thread_id);
            setTitle(truncateText(block.prompt, 30));
          }

          refreshThreads();
          setStatus('READY');
        })
        .catch((err) => {
          console.error(err);
          setStatus('ERROR');
        });
    },
    [refreshThreads, threadID]
  );

  // Update ThreadID
  useEffect(() => {
    if (routeParamID !== 'new' && routeParamID !== threadID) {
      setThreadID(routeParamID);
      setItems([]);
    }
  }, [routeParamID, threadID]);

  // Start New Thread
  useEffect(() => {
    if (
      routeParamID === 'new' &&
      items.length === 1 &&
      items[0].response.length === 0 &&
      status === 'READY'
    ) {
      addResponse(items[items.length - 1]);
    }
  }, [addResponse, items, routeParamID, status]);

  // Load Existing Thread
  useEffect(() => {
    if (!window.location.pathname.includes('/thread/')) return;

    if (
      items.length === 0 &&
      typeof threadID !== 'undefined' &&
      routeParamID === threadID
    ) {
      const savedThread = threads.find(
        (thread: Thread) => thread.id === threadID
      );
      if (savedThread) {
        setTitle(savedThread.title);
        setItems(savedThread.items);
        setFilters(savedThread.filters);
        setStatus('READY');
      } else {
        navigate('/not-found', { replace: true });
      }
    }
  }, [navigate, routeParamID, threadID, threads, items]);

  // Update URL on new ThreadID
  useEffect(() => {
    if (routeParamID === 'new' && threadID) {
      navigate(`/thread/${threadID}`, { replace: true });
    }
  }, [navigate, routeParamID, threadID]);

  // Clean up when not in a thread
  useEffect(() => {
    if (typeof routeParamID === 'undefined') {
      resetThread();
    }
  }, [routeParamID]);

  return (
    <ThreadContext.Provider
      value={{
        id: threadID,
        title,
        items,
        status,
        filters,
        setStatus,
        handleTitleUpdate,
        handlePromptSubmit,
        handleCitationRequest,
        resetThread,
      }}
    >
      {children}
    </ThreadContext.Provider>
  );
};
export { ThreadContext };
